import { useAuth } from '@/contexts/AuthContext';
import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, X, Loader2 } from 'lucide-react';
import { isPaidTier, tierLabel } from '@/lib/tiers';

const EVENT_TYPES = [
  { value: 'teaching', label: 'Teaching / Lessons' },
  { value: 'tournament', label: 'Tournaments' },
  { value: 'casual', label: 'Casual play' },
  { value: 'special', label: 'Special events' },
];
const DISTANCE_OPTIONS = [10, 25, 50, 100, 250];

export default function SettingsPage() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();

  // New-event alert prefs — initialised from the user object once it
  // loads, then mutated in place. Saved via PUT /api/profile/event-alerts.
  const [eaEnabled, setEaEnabled] = useState(true);
  const [eaDistance, setEaDistance] = useState(50);
  const [eaTypes, setEaTypes] = useState(['teaching', 'tournament', 'casual', 'special']);
  const [eaSaving, setEaSaving] = useState(false);
  const [eaTesting, setEaTesting] = useState(false);
  const [snapTesting, setSnapTesting] = useState(false);
  const [emailLog, setEmailLog] = useState(null);

  // Pull the last 5 emails we've sent this user so they can self-
  // diagnose "did that hit my spam folder?" without contacting support.
  useEffect(() => {
    if (!user) return;
    api.get('/profile/email-log')
      .then(({ data }) => setEmailLog(data?.entries || []))
      .catch(() => setEmailLog([]));
  }, [user]);

  const refreshEmailLog = () => {
    api.get('/profile/email-log')
      .then(({ data }) => setEmailLog(data?.entries || []))
      .catch(() => {});
  };
  useEffect(() => {
    if (!user) return;
    setEaEnabled(user.event_alerts_enabled !== false);
    setEaDistance(Number(user.event_alerts_distance) || 50);
    const t = Array.isArray(user.event_alerts_types) && user.event_alerts_types.length > 0
      ? user.event_alerts_types
      : ['teaching', 'tournament', 'casual', 'special'];
    setEaTypes(t);
  }, [user]);

  // Toast the result of a one-click re-enable from the "digest off"
  // confirmation email. The backend always redirects back here so the
  // user lands somewhere sensible whether or not the token was valid.
  useEffect(() => {
    const url = new URL(window.location.href);
    const reenabled = url.searchParams.get('reenabled');
    if (!reenabled) return;
    if (reenabled === '1') {
      toast.success('Daily event digest turned back on — you\'ll see new events again starting tomorrow.');
    } else {
      toast.error('That re-enable link looks invalid or expired. Try the toggle below.');
    }
    url.searchParams.delete('reenabled');
    window.history.replaceState({}, '', url.pathname + (url.search ? url.search : '') + url.hash);
  }, []);

  const toggleEaType = (t) => setEaTypes(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);

  const saveEventAlerts = async () => {
    if (eaEnabled && eaTypes.length === 0) {
      toast.error('Pick at least one event type or turn alerts off.');
      return;
    }
    setEaSaving(true);
    try {
      await api.put('/profile/event-alerts', {
        enabled: eaEnabled,
        distance: eaDistance,
        event_types: eaTypes,
      });
      await refreshUser();
      toast.success('Notification preferences saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setEaSaving(false);
    }
  };

  const sendSampleAlert = async () => {
    setEaTesting(true);
    try {
      const { data } = await api.post('/profile/event-alerts/test-send');
      const matched = data?.result?.sent || 0;
      if (matched > 0) {
        toast.success(`Sample sent to ${data.to} — check your inbox in a minute.`);
      } else {
        const reason = data?.result?.new_events === 0
          ? 'No new events posted in the last 7 days yet.'
          : 'No events matched your zip + radius + type filters this week.';
        toast.info(`No sample sent — ${reason}`);
      }
      refreshEmailLog();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send sample');
    } finally {
      setEaTesting(false);
    }
  };

  const sendSampleSnapshot = async () => {
    setSnapTesting(true);
    try {
      const { data } = await api.post('/profile/weekly-snapshot/test-send');
      const matched = data?.result?.sent || 0;
      if (matched > 0) {
        toast.success(`Sunday snapshot sample sent to ${data.to} — check your inbox in a minute.`);
      } else {
        const reason = data?.result?.upcoming_events === 0
          ? 'No upcoming events in the next 7 days.'
          : 'No events matched your zip + radius + type filters this week.';
        toast.info(`No sample sent — ${reason}`);
      }
      refreshEmailLog();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send sample');
    } finally {
      setSnapTesting(false);
    }
  };

  // Cancel-subscription flow — replaces the old window.confirm() with
  // a proper modal so users see the keep-access-until-period-end
  // messaging before confirming. Reduces the "I cancelled but got
  // charged" support-ticket pattern (see Lauren Stemen 27 Jul 2026).
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      const { data } = await api.post('/subscription/cancel');
      await refreshUser();
      if (data?.self_healed) {
        toast.success(
          'Cancelled. If you were charged recently, our team has been notified to double-check at PayPal.',
          { duration: 6000 },
        );
      } else {
        toast.success('Subscription cancelled. You keep access until the end of your billing period.');
      }
      setCancelConfirmOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel — please email support if this keeps happening.');
    } finally {
      setCancelling(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm('This action cannot be undone. Are you sure?')) return;
    try {
      await logout();
      toast.success('Account deletion requested');
      navigate('/');
    } catch { toast.error('Failed to process request'); }
  };

  return (
    <div data-testid="settings-page" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <h1 className="text-3xl font-bold mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>Settings</h1>

      <div className="space-y-6">
        <Card className="border-slate-100">
          <CardHeader><CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Account</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between py-2">
              <div><p className="font-medium text-sm">Email</p><p className="text-sm text-slate-500">{user?.email}</p></div>
              <Badge variant="outline" className="rounded-full">{user?.email_verified ? 'Verified' : 'Unverified'}</Badge>
            </div>
            <div className="flex items-center justify-between py-2">
              <div><p className="font-medium text-sm">Role</p><p className="text-sm text-slate-500 capitalize">{user?.role}</p></div>
            </div>
            <div className="flex items-center justify-between py-2">
              <div><p className="font-medium text-sm">Member Since</p><p className="text-sm text-slate-500">{user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}</p></div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-100">
          <CardHeader><CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Subscription</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between py-2">
              <div><p className="font-medium text-sm">Current Plan</p><p className="text-sm text-slate-500">{tierLabel(user?.subscription_tier)}</p></div>
              <Badge className={`rounded-full ${isPaidTier(user) ? 'bg-[#56dfb4]/15 text-[#0f172a]' : 'bg-slate-100 text-slate-600'}`}>
                {user?.subscription_status || 'active'}
              </Badge>
            </div>
            <div className="flex gap-2">
              {isPaidTier(user) ? (
                <Button variant="outline" onClick={() => setCancelConfirmOpen(true)} className="rounded-full text-red-500 border-red-200 hover:bg-red-50" data-testid="settings-cancel-btn">
                  Cancel Subscription
                </Button>
              ) : (
                <Button onClick={() => navigate('/subscription')} className="rounded-full bg-[#56dfb4] text-slate-900 hover:bg-[#3ec49b]" data-testid="settings-upgrade-btn">
                  Upgrade Plan
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-100" id="new-event-alerts" data-testid="settings-event-alerts-card">
          <CardHeader>
            <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>New-event alerts</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <>
              <p className="text-sm text-slate-500">
                Once a day, we&apos;ll email you a summary of new Mahjong events posted
                in the previous 24 hours within your alert radius.
              </p>

                <label
                  className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all ${eaEnabled ? 'border-[#56dfb4] bg-[#56dfb4]/5' : 'border-slate-200'}`}
                >
                  <Checkbox
                    checked={eaEnabled}
                    onCheckedChange={v => setEaEnabled(!!v)}
                    className="mt-0.5"
                    data-testid="settings-ea-enabled"
                  />
                  <div>
                    <p className="font-medium text-sm">Send me the daily new-event digest</p>
                    <p className="text-xs text-slate-500">Turn this off to stop receiving the daily email entirely.</p>
                  </div>
                </label>

                {eaEnabled && (
                  <div className="pl-2 space-y-4">
                    <div>
                      <Label className="text-xs mb-1.5 block">Distance from your zip code</Label>
                      <div className="flex flex-wrap gap-2">
                        {DISTANCE_OPTIONS.map(d => (
                          <button
                            key={d}
                            type="button"
                            onClick={() => setEaDistance(d)}
                            className={`px-3 py-1.5 rounded-full text-xs border transition-all ${eaDistance === d ? 'bg-[#56c6df] text-white border-[#56c6df]' : 'border-slate-200 hover:border-slate-300'}`}
                            data-testid={`settings-ea-distance-${d}`}
                          >
                            {d} miles
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs mb-1.5 block">Event types</Label>
                      <div className="flex flex-wrap gap-2">
                        {EVENT_TYPES.map(t => (
                          <button
                            key={t.value}
                            type="button"
                            onClick={() => toggleEaType(t.value)}
                            className={`px-3 py-1.5 rounded-full text-xs border transition-all ${eaTypes.includes(t.value) ? 'bg-[#56c6df] text-white border-[#56c6df]' : 'border-slate-200 hover:border-slate-300'}`}
                            data-testid={`settings-ea-type-${t.value}`}
                          >
                            {t.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-3">
                  <Button
                    onClick={saveEventAlerts}
                    disabled={eaSaving}
                    className="rounded-full bg-[#56dfb4] text-slate-900 hover:bg-[#3ec49b]"
                    data-testid="settings-ea-save-btn"
                  >
                    {eaSaving ? 'Saving…' : 'Save preferences'}
                  </Button>
                  {eaEnabled && (
                    <>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={sendSampleAlert}
                        disabled={eaTesting}
                        className="rounded-full border-[#56dfb4] text-[#0b3a2e] hover:bg-[#56dfb4]/10"
                        data-testid="settings-ea-send-sample-btn"
                      >
                        {eaTesting ? 'Sending…' : 'Send myself a daily-alert sample'}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={sendSampleSnapshot}
                        disabled={snapTesting}
                        className="rounded-full border-[#56c6df] text-[#08334d] hover:bg-[#56c6df]/10"
                        data-testid="settings-snapshot-send-sample-btn"
                      >
                        {snapTesting ? 'Sending…' : 'Send myself a Sunday-snapshot sample'}
                      </Button>
                    </>
                  )}
                </div>
                {eaEnabled && (
                  <p className="text-[11px] text-slate-400 mt-1">
                    Both samples use your saved prefs. The daily-alert sample looks back 7 days and the Sunday-snapshot
                    sample looks ahead 7 days, so each usually has something to preview. Save your changes first if
                    you&apos;ve edited anything above.
                  </p>
                )}

                {/* "Did we send it to you?" log — answers the most common
                    support question ("did this hit my spam?") without
                    requiring a ticket. Mirrors the admin email-log
                    endpoint but scoped to the current user. */}
                <div className="pt-4 mt-2 border-t border-slate-100">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-semibold text-slate-800">
                      Recent emails we&apos;ve sent you
                    </p>
                    <button
                      type="button"
                      onClick={refreshEmailLog}
                      className="text-[11px] text-slate-400 hover:text-slate-600 underline"
                      data-testid="settings-email-log-refresh"
                    >
                      Refresh
                    </button>
                  </div>
                  {emailLog === null ? (
                    <p className="text-xs text-slate-400">Loading…</p>
                  ) : emailLog.length === 0 ? (
                    <p className="text-xs text-slate-400">
                      No emails on file yet. Try the &quot;Send myself a sample&quot; buttons above.
                    </p>
                  ) : (
                    <ul className="space-y-1.5" data-testid="settings-email-log-list">
                      {emailLog.map((e, idx) => {
                        const statusTone =
                          e.status === 'sent' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' :
                          e.status === 'failed' ? 'bg-red-100 text-red-700 border-red-200' :
                          'bg-slate-100 text-slate-500 border-slate-200';
                        const when = e.created_at ? new Date(e.created_at) : null;
                        const whenStr = when ? when.toLocaleString(undefined, {
                          month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
                        }) : '';
                        return (
                          <li
                            key={idx}
                            className="flex items-center justify-between gap-3 text-xs bg-white border border-slate-100 rounded-lg p-2"
                            data-testid={`settings-email-log-row-${idx}`}
                          >
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-slate-800 truncate">{e.subject || '(no subject)'}</p>
                              <p className="text-[11px] text-slate-400">{whenStr}</p>
                              {e.status === 'failed' && e.error && (
                                <p className="text-[10px] text-red-600 font-mono mt-0.5 truncate">{e.error}</p>
                              )}
                            </div>
                            <span
                              className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border flex-shrink-0 ${statusTone}`}
                            >
                              {e.status}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  <p className="text-[10px] text-slate-400 mt-2">
                    <strong>SENT</strong> means Resend accepted the email — if you don&apos;t see it in your inbox, check your spam/promotions folder.
                    <strong> FAILED</strong> means the email never left our server; contact us if you see this repeatedly.
                  </p>
                </div>
            </>
          </CardContent>
        </Card>

        <Card className="border-red-100">
          <CardHeader><CardTitle className="text-lg text-red-500" style={{ fontFamily: 'Outfit, sans-serif' }}>Danger Zone</CardTitle></CardHeader>
          <CardContent>
            <p className="text-sm text-slate-500 mb-3">Once you delete your account, there is no going back.</p>
            <Button variant="outline" onClick={handleDeleteAccount} className="rounded-full text-red-500 border-red-200 hover:bg-red-50" data-testid="settings-delete-btn">
              Delete Account
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Cancel-subscription modal — shared UX pattern with the
          Subscription page. Modal instead of window.confirm() so
          users see the "you keep access until end of billing period"
          contract before agreeing. */}
      {cancelConfirmOpen && (
        <div
          className="fixed inset-0 z-[60] bg-slate-900/70 flex items-start justify-center p-4 overflow-y-auto"
          data-testid="settings-cancel-modal-overlay"
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 mt-16"
            data-testid="settings-cancel-modal"
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-lg flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <AlertTriangle className="w-5 h-5 text-red-500" />
                Cancel subscription?
              </h3>
              <button
                onClick={() => !cancelling && setCancelConfirmOpen(false)}
                className="p-1 hover:bg-slate-100 rounded-full disabled:opacity-50"
                disabled={cancelling}
                aria-label="Close"
                data-testid="settings-cancel-modal-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-slate-600 leading-relaxed mb-3">
              You&apos;ll <strong>keep full access</strong> until the end of your current
              billing period, then drop to the free tier in your track. <strong>No further charges</strong>.
            </p>
            <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 mb-4 text-xs text-amber-900 leading-relaxed">
              If you cancelled directly at PayPal recently and are still seeing charges,
              please email <a href="mailto:meetformahjong@gmail.com" className="underline font-medium">meetformahjong@gmail.com</a>
              — we&apos;ll investigate and refund if appropriate.
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                variant="outline"
                onClick={() => setCancelConfirmOpen(false)}
                disabled={cancelling}
                className="flex-1 rounded-full"
                data-testid="settings-cancel-modal-keep-btn"
              >
                Never mind
              </Button>
              <Button
                onClick={handleCancelSubscription}
                disabled={cancelling}
                className="flex-1 rounded-full bg-red-500 hover:bg-red-600 text-white"
                data-testid="settings-cancel-modal-confirm-btn"
              >
                {cancelling ? (<><Loader2 className="w-4 h-4 animate-spin mr-2" /> Cancelling…</>) : 'Yes, cancel'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
